import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  forwardRef,
  inject,
} from '@angular/core';
import { EllipsisVertical, ExternalLink, Info } from 'lucide-angular';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { NavbarMenuItemComponent } from '../navbar-menu-item/navbar-menu-item.component';

const MORE_ITEM_WIDTH = 100;

@Component({
  selector: 'di-navbar-secondary-menu',
  templateUrl: './navbar-secondary-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    class: 'tw:block tw:w-full',
  },
})
export class NavbarSecondaryMenuComponent implements OnDestroy, AfterViewInit {
  private readonly elRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  private readonly resized$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  private menuItemsWidths: number[] = [];
  private isVisible = false;

  hiddenMenuItems: boolean[] = [];

  readonly icons = { Info, ExternalLink, EllipsisVertical };
  protected readonly moreLabel = $localize`More`;

  @ContentChildren(forwardRef(() => NavbarMenuItemComponent))
  public readonly menuItems!: QueryList<NavbarMenuItemComponent>;

  protected get anyHiddenMenuItem() {
    return this.hiddenMenuItems.some((i) => i);
  }

  ngAfterViewInit(): void {
    this.menuItems.changes.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.isVisible) {
        this.rerenderMenuItems();
      }
    });

    fromEvent(window, 'load')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.isVisible) {
          this.rerenderMenuItems();
        }
      });

    // Use IntersectionObserver to detect when menu becomes visible
    if (typeof window !== 'undefined' && window.IntersectionObserver) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const isNowVisible = entries[0]?.isIntersecting ?? false;
          if (isNowVisible && !this.isVisible) {
            this.isVisible = true;
            this.ngZone.run(() => this.rerenderMenuItems());
          } else if (!isNowVisible) {
            this.isVisible = false;
          }
        },
        { threshold: 0.1 },
      );
      this.intersectionObserver.observe(this.elRef.nativeElement);
    }

    // ResizeObserver for width changes - subscribe first, then observe element
    this.resized$.pipe(debounceTime(100), takeUntil(this.destroyed$)).subscribe(() => {
      if (this.isVisible) {
        this.ngZone.run(() => this.checkResponsiveness());
      }
    });

    // Observe the <ul> element, not the host
    // (host is inside <li> with auto width, but <ul> has position:absolute and resizes with navbar)
    this.setupResizeObserver();
  }

  private setupResizeObserver(): void {
    if (typeof window === 'undefined' || !window.ResizeObserver) return;

    // Wait for the <ul> element to exist
    requestAnimationFrame(() => {
      const ulElement = this.elRef.nativeElement.querySelector<HTMLElement>('ul.second-level');
      if (!ulElement) return;

      this.resizeObserver = new window.ResizeObserver(() => this.resized$.next());
      this.resizeObserver.observe(ulElement);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  rerenderMenuItems(): void {
    this.menuItemsWidths = [];
    this.hiddenMenuItems = [];
    this.cdRef.detectChanges();

    // Wait for render, then measure and check
    requestAnimationFrame(() => {
      this.cacheMenuWidths();
      this.checkResponsiveness();
    });
  }

  private cacheMenuWidths(): void {
    const items = this.elRef.nativeElement.querySelectorAll<HTMLElement>(
      'ul.second-level > li.virtual > a',
    );
    this.menuItemsWidths = Array.from(items).map((e) => e.offsetWidth);
  }

  private checkResponsiveness(): void {
    // Skip if no valid widths (menu not visible)
    if (this.menuItemsWidths.length === 0 || this.menuItemsWidths.every((w) => w === 0)) {
      return;
    }

    // Measure the actual <ul> element, not the host (host is constrained by <li> width,
    // but <ul> has position:absolute and width:100% of navbar-collapse)
    const ulElement = this.elRef.nativeElement.querySelector<HTMLElement>('ul.second-level');
    if (!ulElement) return;

    let changed = false;
    const availableWidth = ulElement.offsetWidth;
    const totalMenuItemsWidth = this.menuItemsWidths.reduce((a, b) => a + b, 0);
    let takenWidth = totalMenuItemsWidth;

    if (availableWidth > takenWidth) {
      this.menuItems.forEach((_, i) => {
        if (this.hiddenMenuItems[i]) {
          this.hiddenMenuItems[i] = false;
          changed = true;
        }
      });
    } else {
      takenWidth = MORE_ITEM_WIDTH;
      this.menuItems.forEach((_, i) => {
        const totalTaken = takenWidth + this.menuItemsWidths[i];
        const doesNotFit = totalTaken > availableWidth;
        if (this.hiddenMenuItems[i] !== doesNotFit) {
          this.hiddenMenuItems[i] = doesNotFit;
          changed = true;
        }
        if (!doesNotFit) {
          takenWidth += this.menuItemsWidths[i];
        }
      });
    }

    if (changed) {
      this.cdRef.detectChanges();
    }
  }
}
